// PTE Core reading + listening task screens, faithful to Pearson interface
// behavior (memory/EXAM_INTERFACE_SPEC.md): dropdown gaps, drag-word gaps,
// re-order boxes with drag AND arrow buttons, yellow single-select, negative
// marking warnings on multi-select, one-shot listening audio with a status box,
// click-to-highlight incorrect words, dictation box, and the 20-30 word
// Summarize Spoken Text screen with its 8-minute task timer.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Headphones, Volume2 } from 'lucide-react';

import { api } from '@/lib/api';
import { FibDropdown, FibDrag, FibTyped, HiwWords, McqOptions, Reorder, ReviewBlock } from '@/components/PTEInteractions';
import { Button } from '@/components/ui/button';
import AssignmentNextCTA from '@/components/AssignmentNextCTA';
import { cn } from '@/lib/utils';

const PHASE = { INTRO: 'INTRO', AUDIO: 'AUDIO', RUN: 'RUN', SUBMITTING: 'SUBMITTING', REVIEW: 'REVIEW', ERROR: 'ERROR' };

const TASK_META = {
  rw_fib: {
    title: 'Reading & Writing: Fill in the Blanks',
    instruction: 'Below is a text with blanks. Click on each blank, a list of choices will appear. Select the appropriate answer choice for each blank.',
    note: 'This task feeds BOTH your Reading and Writing scores.',
  },
  r_mcq_multi: {
    title: 'Multiple Choice, Multiple Answers',
    instruction: 'Read the text and answer the question by selecting all the correct responses. You will need to select more than one response.',
    warning: 'Points are LOST for each wrong selection. Never click an option you cannot justify from the text.',
  },
  r_reorder: {
    title: 'Re-order Paragraphs',
    instruction: 'The text boxes below have been placed in a random order. Restore the original order by dragging the boxes or using the arrow buttons.',
  },
  r_fib: {
    title: 'Reading: Fill in the Blanks',
    instruction: 'In the text below some words are missing. Drag words from the box below to the appropriate place in the text. To undo an answer choice, drag the word back to the box.',
    note: 'There are more words in the box than gaps. Some words will not be used.',
  },
  r_mcq_single: {
    title: 'Multiple Choice, Single Answer',
    instruction: 'Read the text and answer the multiple-choice question by selecting the correct response. Only one response is correct.',
  },
  l_sst: {
    title: 'Summarize Spoken Text',
    instruction: 'You will hear a short recording. Write a summary for a fellow student who was not present. You should write 20-30 words. You have 8 minutes to finish this task.',
    note: 'This task feeds BOTH your Listening and Writing scores. Outside 20-30 words you lose Form points.',
  },
  l_mcq_multi: {
    title: 'Multiple Choice, Multiple Answers',
    instruction: 'Listen to the recording and answer the question by selecting all the correct responses. You will need to select more than one response.',
    warning: 'Points are LOST for each wrong selection.',
  },
  l_fib: {
    title: 'Fill in the Blanks',
    instruction: 'You will hear a recording. Type the missing words in each blank.',
    note: 'Spelling counts. This task feeds BOTH your Listening and Writing scores.',
  },
  l_hcs: {
    title: 'Highlight Correct Summary',
    instruction: 'You will hear a recording. Click on the paragraph that best relates to the recording.',
  },
  l_mcq_single: {
    title: 'Multiple Choice, Single Answer',
    instruction: 'Listen to the recording and answer the multiple-choice question by selecting the correct response. Only one response is correct.',
  },
  l_smw: {
    title: 'Select Missing Word',
    instruction: 'You will hear a recording about a topic. At the end of the recording the last word or group of words has been replaced by a beep. Select the correct option to complete the recording.',
  },
  l_hiw: {
    title: 'Highlight Incorrect Words',
    instruction: 'You will hear a recording. Below is a transcription of the recording. Some words in the transcription differ from what the speaker said. Please click on the words that are different.',
    warning: 'Points are LOST for each wrong click. This task feeds BOTH your Listening and Reading scores.',
  },
  l_wfd: {
    title: 'Write from Dictation',
    instruction: 'You will hear a sentence. Type the sentence in the box below exactly as you hear it. Write as much of the sentence as you can. You will hear the sentence only once.',
    note: 'Every correct word in the correct order scores. This is the highest-leverage listening task: it moves Listening AND Writing.',
  },
};

const wordsOf = (t) => (t || '').trim().split(/\s+/).filter(Boolean).length;

// ---- the runner -------------------------------------------------------------

export default function PTEItemRunner() {
  const { taskType } = useParams();
  const [searchParams] = useSearchParams();
  const setParam = searchParams.get('set');
  const exam = 'pte';
  const navigate = useNavigate();
  const meta = TASK_META[taskType] || { title: 'Practice task', instruction: '' };
  const isListening = taskType.startsWith('l_');

  const [set, setSet] = useState(null);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [score, setScore] = useState(null);
  const [sstText, setSstText] = useState('');
  const [sstSubmission, setSstSubmission] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [error, setError] = useState('');
  const [audioFailed, setAudioFailed] = useState(false);  // slot declared, file not on disk yet
  const [sessionSets, setSessionSets] = useState([]);     // ordered set_ids for continuous flow
  const audioRef = useRef(null);
  const phaseRef = useRef(PHASE.INTRO);

  const isSST = taskType === 'l_sst';
  const setPhaseBoth = (p) => { setPhase(p); phaseRef.current = p; };
  const setResp = (qid, payload) => setResponses((r) => ({ ...r, [qid]: { ...(r[qid] || {}), ...payload } }));

  const load = useCallback(async () => {
    setError(''); setResults(null); setScore(null); setResponses({}); setSstText(''); setSstSubmission(null); setSecondsLeft(null); setAudioFailed(false);
    setPhaseBoth(PHASE.INTRO);
    try {
      const res = await api.practiceSets(exam, taskType, 1, setParam);
      setSet(res.data.sets[0]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No sets for this task type yet.');
      setPhaseBoth(PHASE.ERROR);
    }
  }, [taskType, setParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  // Ordered set list -> continuous session: flow set→set without the picker.
  useEffect(() => {
    api.practiceSetList(exam, taskType)
      .then((r) => setSessionSets((r.data.sets || []).map((s) => s.set_id)))
      .catch(() => setSessionSets([]));
  }, [exam, taskType]);

  const startRun = useCallback(() => {
    setPhaseBoth(PHASE.RUN);
    if (isSST) setSecondsLeft(8 * 60);
  }, [isSST]);

  const begin = useCallback(async () => {
    const audioFile = set?.stimulus?.audio_ref;
    // Transcript-on-screen tasks (HIW / FIB) read while the audio plays.
    const transcriptTask = taskType === 'l_hiw' || taskType === 'l_fib';
    if (isListening) {
      setPhaseBoth(PHASE.AUDIO);
      try {
        if (!audioFile) throw new Error('no audio slot');
        const res = await api.contentAudioBlob(audioFile);
        const url = URL.createObjectURL(res.data);
        const a = new Audio(url);
        audioRef.current = a;
        if (transcriptTask) setPhaseBoth(PHASE.RUN);
        // Recovery paths so a stall/error/no-'ended' event can never trap the
        // student on "Audio is playing" with no way forward. Watchdog fires
        // if `ended` never comes despite the clip loading.
        let watchdog = null;
        const proceed = () => {
          if (watchdog) { clearTimeout(watchdog); watchdog = null; }
          URL.revokeObjectURL(url);
          if (phaseRef.current === PHASE.AUDIO) startRun();
        };
        a.onended = proceed;
        a.onerror = () => { setAudioFailed(true); if (transcriptTask) startRun(); };
        // Belt-and-braces: cap the audio phase at 90 seconds. Real listening
        // clips are under 60s; anything longer is a stall the student can't
        // detect and shouldn't have to sit through.
        watchdog = setTimeout(() => {
          if (phaseRef.current === PHASE.AUDIO) {
            try { a.pause(); } catch { /* ignore */ }
            proceed();
          }
        }, 90_000);
        await a.play();
      } catch {
        // Audio not rendered yet: keep the slot visible. Transcript tasks fall
        // through to their reading view; the rest hold on the inert slot until
        // the listener continues. The clip drops in once it exists at deploy.
        setAudioFailed(true);
        if (transcriptTask) startRun();
      }
    } else {
      startRun();
    }
  }, [set, isListening, taskType, startRun]);

  const submit = useCallback(async () => {
    setPhaseBoth(PHASE.SUBMITTING);
    try {
      if (isSST) {
        const res = await api.submitWriting(exam, { set_id: set.set_id, text: sstText, seconds_used: 8 * 60 - (secondsLeft || 0) });
        setSstSubmission(res.data.submission);
      } else {
        const payload = set.questions.map((q) => ({ qid: q.qid, ...(responses[q.qid] || {}) }));
        const res = await api.submitPractice(exam, { set_id: set.set_id, responses: payload });
        setResults(res.data.results);
        setScore({ earned: res.data.n_correct, total: res.data.n_total });
      }
      setPhaseBoth(PHASE.REVIEW);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed.');
      setPhaseBoth(PHASE.ERROR);
    }
  }, [set, responses, sstText, secondsLeft, isSST]);

  // SST 8-minute task timer (auto-submits, like the real screen)
  useEffect(() => {
    if (!isSST || phase !== PHASE.RUN || secondsLeft == null) return undefined;
    if (secondsLeft <= 0) { submit(); return undefined; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [isSST, phase, secondsLeft, submit]);

  if (phase === PHASE.ERROR) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="max-w-md text-center text-slate-600">{error}</p>
        <Link to="/pte/practice" className="text-sm font-medium text-blue-800 hover:underline">Back to practice</Link>
      </div>
    );
  }
  if (!set) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Preparing…</div>;

  const stim = set.stimulus || {};
  // "Missing" at runtime means: no slot declared, OR the slot's file hasn't been
  // rendered yet (load failed). Either way we surface the transcript / slot.
  const audioMissing = isListening && (!stim.audio_ref || audioFailed);

  // Continuous-session position + the next set to flow into.
  const curId = set.set_id || setParam;
  const pos = sessionSets.indexOf(curId);
  const nextSetId = pos >= 0 && pos < sessionSets.length - 1 ? sessionSets[pos + 1] : null;
  const goNextSet = () => { if (nextSetId) navigate(`/${exam}/practice/${taskType}?set=${nextSetId}`); };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-blue-900 text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            {isListening ? <Headphones className="h-4 w-4 opacity-80" /> : <BookOpen className="h-4 w-4 opacity-80" />}
            <div>
              <p className="text-[11px] uppercase tracking-wider text-blue-200">PTE CORE practice • {isListening ? 'Listening' : 'Reading'}</p>
              <p className="text-sm font-semibold">{meta.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {pos >= 0 && sessionSets.length > 1 && (
              <span className="text-xs font-medium text-blue-200" data-testid="pte-set-pos">Set {pos + 1} of {sessionSets.length}</span>
            )}
            {isSST && phase === PHASE.RUN && secondsLeft != null && (
              <span className="rounded bg-blue-950/60 px-2.5 py-1 text-sm font-semibold tabular-nums" data-testid="sst-timer">
                {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {phase === PHASE.INTRO && (
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">Instructions</p>
            <h1 className="mt-2 text-xl font-semibold text-slate-900">{meta.title}</h1>
            <p className="mt-4 text-[15px] leading-7 text-slate-700" data-testid="pte-item-instruction">{meta.instruction}</p>
            {meta.warning && (
              <p className="mt-3 border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm font-medium text-red-900">{meta.warning}</p>
            )}
            {meta.note && <p className="mt-3 text-sm text-slate-500">{meta.note}</p>}
            {isListening && !audioMissing && (
              <p className="mt-3 text-sm text-slate-500">The recording plays automatically and only once, exactly like test day.</p>
            )}
            {audioMissing && (
              <p className="mt-3 border-l-2 border-amber-400 bg-amber-50/50 px-4 py-3 text-sm text-slate-700">
                Audio for this set is still rendering. The script is shown as text so you can train the task logic meanwhile.
              </p>
            )}
            <div className="mt-10 flex items-center justify-between">
              <Link to="/pte/practice" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="pte-item-exit">
                <ArrowLeft className="h-4 w-4" /> Exit
              </Link>
              <Button size="lg" className="bg-blue-900 px-10 hover:bg-blue-800" data-testid="pte-item-start" onClick={begin}>START</Button>
            </div>
          </div>
        )}

        {phase === PHASE.AUDIO && !audioFailed && (
          <div className="flex flex-col items-center gap-4 border border-slate-300 bg-white px-6 py-14 shadow-sm" data-testid="pte-item-audiobox">
            <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
              <Volume2 className="h-5 w-5 animate-pulse text-blue-900" />
              Audio is playing. It plays only once.
            </div>
            <button
              type="button"
              onClick={startRun}
              data-testid="pte-item-skip-audio"
              className="text-xs text-slate-400 underline hover:text-slate-700"
            >
              Audio not playing? Continue to questions
            </button>
          </div>
        )}

        {phase === PHASE.AUDIO && audioFailed && (
          <div className="flex flex-col items-center gap-5 border border-slate-300 bg-white px-6 py-14 text-center shadow-sm" data-testid="pte-item-audio-pending">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-300">
              <Headphones className="h-6 w-6 text-white" />
            </span>
            <div>
              <p className="font-semibold text-slate-900">The recording plays here.</p>
              <p className="mt-1 max-w-md text-sm text-slate-500">
                On test day this clip plays once and the questions follow. Audio for this set is being
                enabled, it will play automatically here once it is ready.
              </p>
            </div>
            <Button className="bg-blue-900 px-8 hover:bg-blue-800" data-testid="pte-audio-continue" onClick={startRun}>
              Continue to questions
            </Button>
          </div>
        )}

        {(phase === PHASE.RUN || phase === PHASE.SUBMITTING) && (
          <div className="space-y-6">
            {/* stimulus panel */}
            {!isSST && stim.text && !['rw_fib', 'r_fib'].includes(taskType) && (
              <div className="border border-slate-300 bg-white px-6 py-5 shadow-sm">
                <p className="text-[15px] leading-8 text-slate-800" data-testid="pte-item-passage">{stim.text}</p>
              </div>
            )}
            {audioMissing && stim.transcript && taskType !== 'l_hiw' && (
              <div className="border border-amber-200 bg-amber-50/40 px-6 py-4">
                <p className="mono-stat">transcript</p>
                <p className="mt-2 text-[15px] leading-7 text-slate-700">{stim.transcript}</p>
              </div>
            )}

            {/* interaction panel */}
            <div className="border border-slate-300 bg-white px-6 py-6 shadow-sm">
              {taskType === 'rw_fib' && <FibDropdown set={set} responses={responses} setResp={setResp} />}
              {taskType === 'r_fib' && <FibDrag set={set} responses={responses} setResp={setResp} />}
              {taskType === 'r_reorder' && <Reorder set={set} responses={responses} setResp={setResp} />}
              {taskType === 'l_fib' && <FibTyped set={set} responses={responses} setResp={setResp} />}
              {taskType === 'l_hiw' && <HiwWords set={set} responses={responses} setResp={setResp} />}
              {isSST && (
                <div>
                  <textarea
                    className="h-44 w-full resize-none border border-slate-300 p-4 text-[15px] leading-7 text-slate-900 focus:border-blue-900 focus:outline-none"
                    value={sstText}
                    onChange={(e) => setSstText(e.target.value)}
                    placeholder="Write your 20-30 word summary here."
                    data-testid="sst-text"
                  />
                  <p className={cn('mt-2 text-sm tabular-nums', wordsOf(sstText) >= 20 && wordsOf(sstText) <= 30 ? 'text-emerald-700' : 'text-slate-500')} data-testid="sst-wordcount">
                    Total Word Count: {wordsOf(sstText)}
                  </p>
                </div>
              )}
              {taskType === 'l_wfd' && set.questions[0] && (
                <input
                  type="text"
                  className="w-full border border-slate-300 px-4 py-3 text-[15px] text-slate-900 focus:border-blue-900 focus:outline-none"
                  placeholder="Type the sentence you heard."
                  value={responses[set.questions[0].qid]?.text || ''}
                  onChange={(e) => setResp(set.questions[0].qid, { text: e.target.value })}
                  data-testid="wfd-input"
                  autoComplete="off"
                  spellCheck="false"
                />
              )}
              {['r_mcq_multi', 'r_mcq_single', 'l_mcq_multi', 'l_mcq_single', 'l_hcs', 'l_smw'].includes(taskType) &&
                set.questions.map((q) => (
                  <div key={q.qid}>
                    {q.prompt && <p className="mb-4 text-[15px] font-medium text-slate-900">{q.prompt}</p>}
                    <McqOptions q={q} multi={taskType.endsWith('mcq_multi')} responses={responses} setResp={setResp} />
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-between">
              <Link to="/pte/practice" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
                <ArrowLeft className="h-4 w-4" /> Exit
              </Link>
              <Button size="lg" className="bg-blue-900 px-10 hover:bg-blue-800" disabled={phase === PHASE.SUBMITTING} data-testid="pte-item-submit" onClick={submit}>
                {phase === PHASE.SUBMITTING ? 'Scoring…' : 'SUBMIT'}
              </Button>
            </div>
          </div>
        )}

        {phase === PHASE.REVIEW && !isSST && results && (
          <div className="space-y-6">
            <div className="border border-slate-300 bg-white px-6 py-5 shadow-sm">
              <p className="mono-stat">result</p>
              <p className="h-display mt-1 text-3xl" data-testid="pte-item-score">{score.earned} / {score.total}</p>
            </div>
            <ReviewBlock set={set} results={results} />
            <AssignmentNextCTA />
            <div className="flex items-center justify-between">
              <button type="button" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" onClick={() => navigate('/pte/practice')}>
                <ArrowLeft className="h-4 w-4" /> All task types
              </button>
              {nextSetId ? (
                <Button size="lg" className="bg-blue-900 px-8 hover:bg-blue-800" data-testid="pte-item-next" onClick={goNextSet}>Next set →</Button>
              ) : (
                <Button size="lg" className="bg-blue-900 px-8 hover:bg-blue-800" data-testid="pte-item-next"
                  onClick={() => (sessionSets.length > 0 ? navigate(`/${exam}/sets/${taskType}`) : load())}>
                  {sessionSets.length > 0 ? 'All sets done, back to list' : 'Next set'}
                </Button>
              )}
            </div>
          </div>
        )}

        {phase === PHASE.REVIEW && isSST && sstSubmission && (
          <div className="space-y-6">
            <div className="border border-slate-300 bg-white px-6 py-5 shadow-sm">
              <p className="mono-stat">submitted · {sstSubmission.word_count} words</p>
              {sstSubmission.status === 'gate_zeroed' ? (
                <p className="mt-2 border-l-2 border-rose-500 bg-rose-50/60 px-4 py-3 text-sm font-medium text-rose-900" data-testid="sst-gate">
                  Outside the 20&ndash;30 word range, this response scores 0 on the real exam.
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-600" data-testid="sst-status">
                  {sstSubmission.form_score === 2 ? 'Length: full credit (20–30 words).' : 'Length: partial credit. Aim for 20–30 words.'} Your detailed
                  feedback {sstSubmission.status === 'scored' ? 'is ready under Results.' : 'will appear under Results once it’s marked.'}
                </p>
              )}
            </div>
            <AssignmentNextCTA />
            <div className="flex items-center justify-between">
              <button type="button" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" onClick={() => navigate('/pte/practice')}>
                <ArrowLeft className="h-4 w-4" /> All task types
              </button>
              {nextSetId ? (
                <Button size="lg" className="bg-blue-900 px-8 hover:bg-blue-800" onClick={goNextSet}>Next prompt →</Button>
              ) : (
                <Button size="lg" className="bg-blue-900 px-8 hover:bg-blue-800"
                  onClick={() => (sessionSets.length > 0 ? navigate(`/${exam}/sets/${taskType}`) : load())}>
                  {sessionSets.length > 0 ? 'All prompts done, back to list' : 'Next prompt'}
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
